import { ReactNode, type MouseEvent, type ButtonHTMLAttributes } from 'react';

type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'danger'
  | 'warning'
  | 'ghost'
  | 'outline'
  | 'dangerOutline'
  | 'link'
  | 'linkDanger'
  | 'linkMuted'
  | 'onDark';
type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'type' | 'className' | 'children' | 'title' | 'disabled'> {
  children: ReactNode;
  /**
   * Receives the click event. A plain `() => void` handler is still valid — a
   * function that ignores an argument is assignable to one that supplies it —
   * but a button inside a clickable table row needs `stopPropagation`, and
   * without the event that button could only be left as a bare `<button>`.
   */
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  variant?: ButtonVariant;
  size?: ButtonSize;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
  icon?: ReactNode;
  fullWidth?: boolean;
  /** Native tooltip. Needed wherever a compact button carries an explanation
   *  that won't fit in its label. */
  title?: string;
  /** Accessible name, for icon-only buttons where `children` is not text. */
  'aria-label'?: string;
  /** A toggle that is on (e.g. Filters open). Sets aria-pressed and the "on" look. */
  pressed?: boolean;
  /** For a button that shows or hides a region. */
  'aria-expanded'?: boolean;
}

export default function Button({
  children,
  onClick,
  variant = 'primary',
  size = 'md',
  disabled = false,
  type = 'button',
  className = '',
  icon,
  fullWidth = false,
  title,
  'aria-label': ariaLabel,
  pressed,
  'aria-expanded': ariaExpanded,
  ...rest
}: ButtonProps) {
  // `focus-visible` rather than `focus` so a mouse click doesn't leave a ring
  // behind, and an `active:` state so presses feel acknowledged.
  const baseClasses =
    'inline-flex items-center justify-center gap-2 font-medium rounded-md transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

  const variantClasses = {
    primary:
      'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 focus-visible:ring-blue-500',
    secondary:
      'bg-gray-600 text-white hover:bg-gray-700 active:bg-gray-800 focus-visible:ring-gray-500',
    success:
      'bg-green-600 text-white hover:bg-green-700 active:bg-green-800 focus-visible:ring-green-500',
    danger: 'bg-red-600 text-white hover:bg-red-700 active:bg-red-800 focus-visible:ring-red-500',
    ghost:
      'bg-transparent text-gray-700 hover:bg-gray-100 active:bg-gray-200 focus-visible:ring-gray-500',
    outline:
      'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 active:bg-gray-100 focus-visible:ring-blue-500',
    warning:
      'bg-amber-600 text-white hover:bg-amber-700 active:bg-amber-800 focus-visible:ring-amber-500',
    dangerOutline:
      'bg-white border border-red-300 text-red-700 hover:bg-red-50 active:bg-red-100 focus-visible:ring-red-500',
    // Text actions that sit inline ("Edit", "Remove", "Back to login"): no box, no padding.
    link: 'text-blue-700 hover:text-blue-800 hover:underline focus-visible:ring-blue-500',
    linkDanger: 'text-red-600 hover:text-red-700 hover:underline focus-visible:ring-red-500',
    linkMuted: 'text-gray-600 hover:text-gray-800 hover:underline focus-visible:ring-gray-500',
    // On a dark or coloured surface (image viewer, chat header, gradient banners).
    onDark: 'bg-white/10 text-white hover:bg-white/20 active:bg-white/30 focus-visible:ring-white',
  };

  const sizeClasses = {
    xs: 'px-2.5 py-1.5 text-sm',
    sm: 'px-3 py-2 text-base',
    md: 'px-4 py-2.5 text-base',
    lg: 'px-6 py-3 text-lg',
  };

  // Links take the text size only, so they line up with the text around them.
  const linkSizeClasses = { xs: 'text-sm', sm: 'text-base', md: 'text-base', lg: 'text-lg' };

  const widthClass = fullWidth ? 'w-full' : '';
  const pressedClass = pressed ? 'border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100' : '';

  return (
    <button
      {...rest}
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={ariaLabel}
      aria-pressed={pressed ?? rest['aria-pressed']}
      aria-expanded={ariaExpanded}
      className={`${baseClasses} ${variantClasses[variant]} ${variant.startsWith('link') ? linkSizeClasses[size] : sizeClasses[size]} ${widthClass} ${pressedClass} ${className}`}
    >
      {icon && <span className="flex-shrink-0">{icon}</span>}
      {children}
    </button>
  );
}
